import { readableTextOn, softTintOn } from '@/utils/branding';
import type { BrandProfile, BrandRole } from '@/types/portal';

interface BrandPreviewPanelProps {
  profile: BrandProfile;
  colours: Record<BrandRole, string>;
  presetName: string;
  /** Uploaded logos keyed by asset id (data URLs). */
  logos: Record<string, string>;
}

/** A live, colour-accurate preview of the portal header and an email signature. */
export default function BrandPreviewPanel({
  profile,
  colours,
  presetName,
  logos,
}: BrandPreviewPanelProps) {
  const onPrimary = readableTextOn(colours.primary);
  const onAccent = readableTextOn(colours.accent);
  const mark = logos.mark || logos.avatar;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">Live preview</h3>
          <p className="mt-0.5 text-xs text-foreground-500">How your brand renders right now</p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
          <i className="ri-eye-line" />
        </span>
      </header>

      <div className="space-y-4 p-5">
        {/* Portal header */}
        <div className="overflow-hidden rounded-md border border-background-200">
          <div
            className="flex items-center gap-2 px-3 py-2.5"
            style={{ backgroundColor: colours.primary }}
          >
            <span
              className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-md text-[11px] font-bold"
              style={{ backgroundColor: softTintOn(onPrimary === '#FFFFFF' ? '#FFFFFF' : '#000000', 0.18), color: onPrimary }}
            >
              {mark ? (
                <img src={mark} alt={`${profile.name || 'Agency'} mark`} className="h-full w-full object-contain" />
              ) : (
                profile.logoInitials || '··'
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold" style={{ color: onPrimary }}>
                {profile.name || 'Your agency'}
              </span>
              <span className="block truncate text-[10px]" style={{ color: softTintOn(onPrimary, 0.75) }}>
                powered by Twiva Pay
              </span>
            </span>
            <span className="hidden gap-1 sm:flex">
              <span className="h-1.5 w-5 rounded-full" style={{ backgroundColor: softTintOn(onPrimary, 0.35) }} />
              <span className="h-1.5 w-5 rounded-full" style={{ backgroundColor: softTintOn(onPrimary, 0.35) }} />
            </span>
          </div>

          <div className="space-y-2 bg-background-100 p-3">
            <div className="rounded-md border border-background-200 bg-background-50 p-2.5">
              <p className="text-[11px] font-medium text-foreground-900">Autumn skincare launch</p>
              <p className="mt-0.5 text-[10px] text-foreground-500">4 creators · Kenya &amp; Uganda</p>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: softTintOn(colours.accent, 0.16), color: colours.accent }}
                >
                  Approved
                </span>
                <span
                  className="ml-auto rounded-md px-2.5 py-1 text-[10px] font-medium"
                  style={{ backgroundColor: colours.primary, color: onPrimary }}
                >
                  View report
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Palette row */}
        <div className="flex items-center gap-2">
          {(['primary', 'accent', 'secondary'] as BrandRole[]).map((role) => (
            <div key={role} className="flex-1">
              <span
                className="block h-8 w-full rounded-md border border-background-200"
                style={{ backgroundColor: colours[role] }}
              />
              <p className="mt-1 text-center text-[10px] capitalize text-foreground-500">{role}</p>
            </div>
          ))}
        </div>

        {/* Email signature */}
        <div className="rounded-md border border-background-200 bg-background-50 p-3">
          <p className="text-[10px] font-label uppercase tracking-wider text-foreground-400">
            Email preview
          </p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-md text-[10px] font-bold"
              style={{ backgroundColor: colours.primary, color: onPrimary }}
            >
              {mark ? (
                <img src={mark} alt={`${profile.name || 'Agency'} mark`} className="h-full w-full object-contain" />
              ) : (
                profile.logoInitials || '··'
              )}
            </span>
            <span className="text-xs font-semibold text-foreground-900">
              {profile.name || 'Your agency'}
            </span>
          </div>
          <p className="mt-2 text-[11px] font-medium text-foreground-800">
            Your payment is ready for approval
          </p>
          <p className="mt-0.5 text-[10px] text-foreground-500">
            {profile.tagline || 'Add a tagline to see it here.'}
          </p>
          <span
            className="mt-2 inline-block rounded-md px-2.5 py-1 text-[10px] font-medium"
            style={{ backgroundColor: colours.accent, color: onAccent }}
          >
            Review payment
          </span>
          <p className="mt-2 text-[10px] text-foreground-400">
            {profile.supportEmail} · {profile.website}
          </p>
        </div>

        <div className="flex items-center justify-between border-t border-background-100 pt-3 text-[11px] text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-palette-line" />
            {presetName}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-global-line" />
            {profile.domain || 'No domain yet'}
          </span>
        </div>
      </div>
    </section>
  );
}